import { Divider, Menu, MenuItem, MenuProps, Tooltip } from '@mui/material';
import classNames from 'classnames';
import React, { MouseEvent, useState } from 'react';
import css from './BaseMenu.module.scss';

interface BaseMenuProps extends Omit<MenuProps, 'children' | 'anchorEl' | 'open' | 'onClose'> {
  children: React.ReactNode;
  className?: string;
  trigger?: React.ReactNode;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  // Allow manual control if needed
  anchorEl?: HTMLElement | null;
  open?: boolean;
  onClose?: (event: MouseEvent<HTMLElement>) => void;
}

interface MenuItemProps {
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  danger?: boolean;
  className?: string;
  /**
   * Why the row is dimmed. Rendered as a tooltip AND as the accessible name, and ignored
   * unless `disabled`.
   *
   * Menus used to render no reason at all, which was fine while every disabled verb also
   * appeared on a `VerbRail` that did. `MediaActionBar` broke that assumption: it is the only
   * place the media verbs appear in the moment preview, so a still-processing media showed
   * three greyed rows with nothing anywhere to say why — and the reason string was being
   * computed and thrown away.
   *
   * Both, not just the tooltip, for the reason `VerbRail` documents at length: a disabled
   * element fires no hover or focus events, so the tooltip has to hang off a wrapper span,
   * which leaves it mouse-only. Safe to use as the name because every reason LEADS with the
   * action (see the `<Label> — …` builders in `@lib/verbs`), so the row still announces what it
   * does before explaining itself.
   */
  disabledReason?: string;
}

export const BaseMenu: React.FC<BaseMenuProps> = ({
  children,
  className,
  trigger,
  onMenuOpen,
  onMenuClose,
  anchorEl: externalAnchorEl,
  open: externalOpen,
  onClose: externalOnClose,
  ...props
}) => {
  const [internalAnchorEl, setInternalAnchorEl] = useState<null | HTMLElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const anchorEl = externalAnchorEl ?? internalAnchorEl;
  const open = externalOpen ?? internalOpen;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!externalAnchorEl) {
      setInternalAnchorEl(event.currentTarget);
      setInternalOpen(true);
    }
    onMenuOpen?.();
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!externalAnchorEl) {
      setInternalAnchorEl(null);
      setInternalOpen(false);
    }
    onMenuClose?.();
    externalOnClose?.(event);
  };

  return (
    <>
      {trigger && <div onClick={handleClick}>{trigger}</div>}
      <Menu
        {...props}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={classNames(css.baseMenu, className)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          ...props.slotProps,
          paper: (ownerState) => {
            const paper =
              typeof props.slotProps?.paper === 'function'
                ? props.slotProps.paper(ownerState)
                : props.slotProps?.paper;
            return {
              ...paper,
              sx: [
                { zIndex: 1400 },
                ...(Array.isArray(paper?.sx) ? paper.sx : paper?.sx ? [paper.sx] : []),
              ],
            };
          },
        }}
      >
        {children}
      </Menu>
    </>
  );
};

// Convenience component for menu items with icons
export const BaseMenuItem: React.FC<MenuItemProps> = ({
  icon,
  children,
  onClick,
  disabled = false,
  danger = false,
  className,
  disabledReason,
}) => {
  const item = (
    <MenuItem
      onClick={onClick}
      disabled={disabled}
      // The reason replaces the name only while disabled, and only when there is one — an
      // enabled row keeps its text content as its name.
      aria-label={disabled && disabledReason ? disabledReason : undefined}
      className={classNames(css.menuItem, danger && css.dangerItem, className)}
    >
      <span className={css.menuItemContent}>
        {icon && <span className={css.menuIcon}>{icon}</span>}
        {children}
      </span>
    </MenuItem>
  );

  if (!disabled || !disabledReason) return item;

  // The span is required: MUI sets `pointer-events: none` on a disabled `MenuItem`, so a
  // tooltip anchored to the item itself never fires. Wrapping is safe for keyboard navigation
  // because `MenuList` skips disabled items anyway.
  return (
    <Tooltip title={disabledReason} placement="left" arrow>
      <span>{item}</span>
    </Tooltip>
  );
};

/**
 * A hairline between two groups of items, with no heading over it.
 *
 * The quiet form of `BaseMenuTitle`, for a menu whose groups don't need naming. The moment
 * modal's kebab uses it: its top group is the verbs and its bottom group is housekeeping, and
 * "Media settings" over the whole thing would have been a title the top half contradicts.
 *
 * `BaseMenuTitle` renders this rather than its own `<Divider />`, so the two can't drift.
 */
export const BaseMenuDivider: React.FC = () => <Divider />;

// Convenience component for menu titles
export const BaseMenuTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <>
    <span className={classNames(css.menuTitle, className)}>{children}</span>
    <BaseMenuDivider />
  </>
);
